import type { PixelCanvas } from '../pixel.js';
import { box, drawBox, drawBoxes, drawCylinder, faceQuad, faces, frontFace, iso, turn, turnBox } from './iso.js';
import type { BoxPiece, FurniDrawer } from './iso.js';

/*
 * Stadium furniture: a real running track, bleachers, floodlights, a
 * scoreboard, the finish line, starting blocks, an entrance arch, a
 * winners' podium and corner flags. Owned by the stadium/pets features.
 */

function diamond(pc: PixelCanvas, r: number, color: string): void {
  pc.poly([iso(-r, -r), iso(r, -r), iso(r, r), iso(-r, r)], color);
}

/* -------------------------------------------------------------------------- */
/* Running track                                                              */
/* -------------------------------------------------------------------------- */

const TRACK_BASE = '#b9432e';
const TRACK_BASE_LIGHT = '#cf5b3f';
const TRACK_LINE = '#fdf6e8';
/** Three lane-divider offsets, shared by the straight and corner pieces. */
const LANES = [-0.16, 0, 0.16] as const;

function trackTile(pc: PixelCanvas): void {
  diamond(pc, 0.5, TRACK_BASE);
  diamond(pc, 0.42, TRACK_BASE_LIGHT);
}

const drawRunningTrackStraight: FurniDrawer = (pc, rotation) => {
  trackTile(pc);
  // Lane lines; which axis they run along depends on facing.
  const along = rotation === 2 || rotation === 6;
  for (const offset of LANES) {
    const a = along ? iso(offset, -0.42) : iso(-0.42, offset);
    const b = along ? iso(offset, 0.42) : iso(0.42, offset);
    pc.line(a.x, a.y, b.x, b.y, TRACK_LINE);
  }
};

/** The two tile edges a corner piece connects, one pair per facing. */
const CORNER_EDGES: Record<number, readonly [readonly [number, number], readonly [number, number]]> = {
  0: [[-0.42, 0], [0, -0.42]],
  2: [[0, -0.42], [0.42, 0]],
  4: [[0.42, 0], [0, 0.42]],
  6: [[0, 0.42], [-0.42, 0]],
};

const drawRunningTrackCorner: FurniDrawer = (pc, rotation) => {
  trackTile(pc);
  const [[u1, v1], [u2, v2]] = CORNER_EDGES[rotation ?? 0] ?? CORNER_EDGES[0]!;
  for (const offset of LANES) {
    const a = iso(u1 + offset, v1 + offset);
    const mid = iso(offset, offset);
    const b = iso(u2 + offset, v2 + offset);
    pc.line(a.x, a.y, mid.x, mid.y, TRACK_LINE);
    pc.line(mid.x, mid.y, b.x, b.y, TRACK_LINE);
  }
};

/* -------------------------------------------------------------------------- */
/* Stands, lights and signage                                                 */
/* -------------------------------------------------------------------------- */

const drawBleacher: FurniDrawer = (pc, rotation) => {
  const riser = faces('#5b6672');
  const seat = faces('#8b98a6');
  const tiers: Array<[number, number, number]> = [
    [0.16, 0.46, 10],
    [-0.14, 0.16, 20],
    [-0.44, -0.14, 32],
  ];
  const pieces: BoxPiece[] = [];
  for (const [v0, v1, h] of tiers) {
    const [u0, u1, tv0, tv1] = turnBox(-0.46, 0.46, v0, v1, rotation);
    pieces.push(box(u0, u1, tv0, tv1, 0, h, riser));
    pieces.push(box(u0, u1, tv0, tv1, h - 3, h, seat));
  }
  drawBoxes(pc, pieces);
};

const drawFloodlight: FurniDrawer = (pc, rotation) => {
  drawBox(pc, box(-0.16, 0.16, -0.16, 0.16, 0, 6, faces('#4a4f57')));
  drawCylinder(pc, 0.05, 6, 62, '#9aa3ab', '#7b828a');
  // The head leans toward whichever face is lit, so the beam reads as aimed at the field.
  const lean = frontFace(rotation) === 'right' ? 6 : -6;
  const head = iso(0, 0, 66);
  pc.rect(head.x - 10 + lean, head.y - 8, 20, 10, '#33363c');
  for (let row = 0; row < 2; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      pc.ellipse(head.x - 8 + lean + col * 5, head.y - 6 + row * 5, 1.6, 1.6, '#ffe58a');
    }
  }
  pc.ellipse(head.x + lean, head.y - 4, 14, 5, '#ffe58a30');
};

const drawScoreboard: FurniDrawer = (pc, rotation) => {
  drawBox(pc, box(-0.44, 0.44, -0.1, 0.1, 0, 4, faces('#4a4f57')));
  drawBox(pc, box(-0.4, 0.4, -0.06, 0.06, 4, 46, faces('#2e3238')));
  const face = frontFace(rotation) ?? 'left';
  faceQuad(pc, face, 0.06, -0.36, 0.36, 8, 42, '#12151a');
  const cols = 7;
  const rows = 4;
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const s = -0.32 + c * (0.64 / (cols - 1));
      const h = 12 + r * 7;
      const p = face === 'left' ? iso(s, 0.06, h) : iso(0.06, s, h);
      pc.px(p.x, p.y, (r + c) % 3 === 0 ? '#ffd94a' : '#3a3f46');
    }
  }
};

const drawFinishLineBanner: FurniDrawer = (pc, rotation) => {
  const [u1, v1] = turn(-0.42, 0, rotation);
  const [u2, v2] = turn(0.42, 0, rotation);
  drawCylinder(pc, 0.04, 0, 34, '#e6e6e6', '#c7c7c7', u1, v1);
  drawCylinder(pc, 0.04, 0, 34, '#e6e6e6', '#c7c7c7', u2, v2);
  const a = iso(u1, v1, 34);
  const b = iso(u2, v2, 34);
  const size = 6;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const cells = Math.max(1, Math.round(Math.hypot(dx, dy) / size));
  for (let i = 0; i < cells; i += 1) {
    const t = (i + 0.5) / cells;
    const color = i % 2 === 0 ? '#1a1a1a' : '#fffaf0';
    pc.rect(a.x + dx * t - size / 2, a.y + dy * t - 4, size, 8, color);
  }
};

const drawStartingBlocks: FurniDrawer = (pc, rotation) => {
  const pt = (u: number, v: number, h: number) => {
    const [tu, tv] = turn(u, v, rotation);
    return iso(tu, tv, h);
  };
  for (const offset of [-0.18, 0, 0.18]) {
    pc.poly(
      [pt(offset - 0.06, 0.28, 0), pt(offset + 0.06, 0.28, 0), pt(offset + 0.06, 0.12, 6), pt(offset - 0.06, 0.12, 6)],
      '#3a4cb0',
    );
    pc.poly(
      [pt(offset - 0.06, 0.12, 6), pt(offset + 0.06, 0.12, 6), pt(offset + 0.06, 0.12, 9), pt(offset - 0.06, 0.12, 9)],
      '#2c3a8f',
    );
  }
};

const drawStadiumArch: FurniDrawer = (pc, rotation) => {
  const post = faces('#c9a13a');
  const topColor = faces('#f0bf36');
  const [pl0, pl1, plv0, plv1] = turnBox(-0.42, -0.3, -0.1, 0.1, rotation);
  const [pr0, pr1, prv0, prv1] = turnBox(0.3, 0.42, -0.1, 0.1, rotation);
  const [tu0, tu1, tv0, tv1] = turnBox(-0.42, 0.42, -0.1, 0.1, rotation);
  drawBoxes(pc, [
    box(pl0, pl1, plv0, plv1, 0, 60, post),
    box(pr0, pr1, prv0, prv1, 0, 60, post),
    box(tu0, tu1, tv0, tv1, 60, 70, topColor),
  ]);
  const c = iso(0, 0, 78);
  pc.ellipse(c.x, c.y, 10, 5, '#fffaf0');
};

const drawWinnersPodium: FurniDrawer = (pc, rotation) => {
  const silver = faces('#c7c7c7');
  const gold = faces('#f0bf36');
  const bronze = faces('#c9863f');
  const [su0, su1, sv0, sv1] = turnBox(-0.42, -0.16, -0.15, 0.15, rotation);
  const [gu0, gu1, gv0, gv1] = turnBox(-0.15, 0.15, -0.15, 0.15, rotation);
  const [bu0, bu1, bv0, bv1] = turnBox(0.16, 0.42, -0.15, 0.15, rotation);
  drawBoxes(pc, [
    box(su0, su1, sv0, sv1, 0, 18, silver),
    box(gu0, gu1, gv0, gv1, 0, 26, gold),
    box(bu0, bu1, bv0, bv1, 0, 12, bronze),
  ]);
  const top = iso(0, 0, 26);
  pc.px(top.x, top.y - 2, '#fffaf0');
};

const drawStadiumFlag: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.03, 0, 30, '#e6e6e6', '#c7c7c7');
  const top = iso(0, 0, 30);
  pc.poly([{ x: top.x, y: top.y }, { x: top.x + 10, y: top.y + 3 }, { x: top.x, y: top.y + 7 }], '#d9433b');
};

/** Drawers for stadium furniture. */
export const STADIUM_DRAWERS: Record<string, FurniDrawer> = {
  running_track_straight: drawRunningTrackStraight,
  running_track_corner: drawRunningTrackCorner,
  stadium_bleacher: drawBleacher,
  stadium_floodlight: drawFloodlight,
  stadium_scoreboard: drawScoreboard,
  finish_line_banner: drawFinishLineBanner,
  starting_blocks: drawStartingBlocks,
  stadium_arch: drawStadiumArch,
  winners_podium: drawWinnersPodium,
  stadium_flag: drawStadiumFlag,
};
